import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logoFB from '../../assets/review/logoFB.svg';
import logoGMB from '../../assets/review/logoGMB.svg';
import logoOS from '../../assets/review/logoOS.svg';
import { Review, ReviewsListProps, REVIEWS_PER_PAGE } from '../type';
import loader from '../../assets/loader.png';
import ReviewCard from './ReviewCard';

const ReviewsList: React.FC<ReviewsListProps> = ({
    openModal,
    reviews,
    isLoading,
    onReviewsUpdate,
    currentPage,
    setCurrentPage,
}) => {
    const navigate = useNavigate();
    const indexOfLastItem = currentPage * REVIEWS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - REVIEWS_PER_PAGE;
    const currentReviews = reviews.slice(indexOfFirstItem, indexOfLastItem);

    const [viewedReviews, setViewedReviews] = useState<string[]>([]);
    const [pendingUpdates] = useState<NodeJS.Timeout[]>([]);
    const [visuallyNewReviews, setVisuallyNewReviews] = useState<string[]>([]);

    const currentReviewsRef = useRef(currentReviews);
    const visuallyNewReviewsRef = useRef(visuallyNewReviews);
    const pendingUpdatesRef = useRef(pendingUpdates);

    currentReviewsRef.current = currentReviews;
    visuallyNewReviewsRef.current = visuallyNewReviews;
    pendingUpdatesRef.current = pendingUpdates;

    const getNetworkLogo = (network: string) => {
        switch (network) {
            case 'facebook':
                return logoFB;
            case 'google_my_business':
                return logoGMB;
            case 'opinion_system':
                return logoOS;
        }
    };

    const handleOpinionSystemRedirect = (reviewUrl: string) => {
        window.open(reviewUrl, '_blank', 'noopener,noreferrer');
    };

    const handleShare = (review: Review) => {
        navigate(`/share-review/${review.id}`, {
            state: { review },
        });
    };

    useEffect(() => {
        const unreadReviewsOnPage = currentReviews.filter(
            review => review.status === 'unread' && !review.hasBeenRead,
        );

        if (unreadReviewsOnPage.length > 0) {
            const unreadIds = unreadReviewsOnPage.map(r => r.id);

            setVisuallyNewReviews(prev => {
                const newIds = unreadIds.filter(id => !prev.includes(id));
                return newIds.length > 0 ? [...prev, ...newIds] : prev;
            });

            const newViewedIds = unreadIds.filter(id => !viewedReviews.includes(id));
            if (newViewedIds.length > 0) {
                setViewedReviews(prev => [...prev, ...newViewedIds]);
            }
        }
    }, [currentReviews, currentPage, onReviewsUpdate, reviews.length, viewedReviews]);

    useEffect(() => {
        return () => {
            const urlParams = new URLSearchParams(window.location.search);
            const statusFilter = urlParams.get('status');

            const currentReviewsAtCleanup = currentReviewsRef.current;
            const pendingUpdatesAtCleanup = pendingUpdatesRef.current;

            const reviewsToMarkAsRead = currentReviewsAtCleanup.filter(
                review =>
                    review.status === 'unread' && !review.hasBeenRead && statusFilter !== 'unread',
            );

            if (reviewsToMarkAsRead.length > 0) {
                onReviewsUpdate(reviewsToMarkAsRead);

                setVisuallyNewReviews(prev =>
                    prev.filter(id => !reviewsToMarkAsRead.some(review => review.id === id)),
                );
            }

            pendingUpdatesAtCleanup.forEach(timeoutId => clearTimeout(timeoutId));
        };
    }, [currentPage, onReviewsUpdate]);

    useEffect(() => {
        setCurrentPage(currentPage ?? 1);
    }, [reviews, currentPage, setCurrentPage]);

    useEffect(() => {
        if (reviews.length > 0) {
            setVisuallyNewReviews(prev =>
                prev.filter(id => reviews.some(review => review.id === id)),
            );
        }
    }, [reviews]);

    if (isLoading) {
        return (
            <div className="w-full h-[200px] flex justify-center items-center">
                <img
                    src={loader}
                    alt="Chargement..."
                    className="w-[50px] h-[50px] mt-[15px] animate-spin"
                />
            </div>
        );
    }

    return (
        <>
            {currentReviews.map((review: Review) => (
                <ReviewCard
                    key={review.id}
                    review={review}
                    isNew={visuallyNewReviews.includes(review.id)}
                    getNetworkLogo={getNetworkLogo}
                    onShare={handleShare}
                    onReply={openModal}
                    onOpinionSystemRedirect={handleOpinionSystemRedirect}
                />
            ))}
        </>
    );
};

export default ReviewsList;
